import React, { createContext, useContext } from 'react';
import { makeAutoObservable } from 'mobx';
import {
  CreatorRole,
  GetChangeDto,
  ListWorkMaterialDto,
  ListWorkPackageDto,
  ListWorkServiceDto,
} from '@codefluegel/zeta-change-typescript-client';

import { ReducedWorkPackagesType } from '../types/workPackages';
import { ReducedServicesType } from '../types/services';
import { ReducedMaterialsType } from '../types/materials';
import { getDepartmentById } from '../utils/utils';
import CalculationStore from './calculationStore';

export interface IChangeStore {
  change: GetChangeDto | null;
  workPackages: ListWorkPackageDto[];
  workServices: ListWorkServiceDto[];
  workMaterials: ListWorkMaterialDto[];
  reducedWorkPackages: ReducedWorkPackagesType;
  reducedServices: ReducedServicesType;
  reducedWorkMaterials: ReducedMaterialsType;
  user: string;
  isDataLoading: boolean;
  // getDataRequest: (changeId: number) => void;
  // getWorkPackages: (changeId: number) => void;
  setUpdatedChange: (updatedChange: GetChangeDto) => void;
  setUser: (updatedChange: string) => void;
  pushNewWorkPackage: (newWorkPackage: ListWorkPackageDto) => void;
  pushNewServicePackage: (newServicePackage: ListWorkServiceDto) => void;
  pushNewMaterialPackage: (newMaterialPackage: ListWorkMaterialDto) => void;
  removeServicePackage: (servicePackageId: number) => void;
  replaceUpdatedServicePackage: (servicePackage: ListWorkServiceDto) => void;
  replaceUpdatedWorkPackage: (workPackage: ListWorkPackageDto) => void;
  removeWorkPackage: (workPackageId: number) => void;
  removeMaterialPackage: (materialPackageId: number) => void;
  replaceUpdatedMaterialPackage: (materialPackage: ListWorkMaterialDto) => void;
  // removeProcessFromChange: (changeId: number, processId: string) => void;
  // addProcessToChange: (changeRequestId: number, departmentId: string) => void;
  setWorkPackages: (workPackages: ListWorkPackageDto[]) => void;
  setWorkServices: (workServices: ListWorkServiceDto[]) => void;
  setWorkMaterials: (workMaterials: ListWorkMaterialDto[]) => void;
  setIsDataLoading: (flag: boolean) => void;
  setChange: (change: GetChangeDto | null) => void;
  resetStore: () => void;
  totalWorkPackageHoursOfUsedProcesses: number;
  getTotalWorkServicesHoursOfUsedProcesses: (
    creatorRole: CreatorRole | undefined,
  ) => number;
  getTotalWorkMaterialHoursOfUsedProcesses: (
    creatorRole: CreatorRole | undefined,
  ) => number;
  getCalculatedHoursCosts: (released: boolean) => number;
  getCalculatedHours: (released: boolean) => number;
  calculationStore: CalculationStore;
}

export class ChangeStore implements IChangeStore {
  change: GetChangeDto | null = null;

  workPackages: ListWorkPackageDto[] = [];

  workMaterials: ListWorkMaterialDto[] = [];

  workServices: ListWorkServiceDto[] = [];

  user = '';

  public calculationStore: CalculationStore;

  isDataLoading = false;
  // notification: NotificationType | null = null;

  constructor() {
    makeAutoObservable(this);
    this.calculationStore = new CalculationStore(this);
  }

  setIsDataLoading = (flag: boolean) => {
    this.isDataLoading = flag;
  };

  setChange = (change: GetChangeDto | null) => {
    this.change = change;
  };

  setUser = (currentUser: string) => {
    this.user = currentUser;
  };

  setWorkPackages = (workPackages: ListWorkPackageDto[] | []) => {
    this.workPackages = workPackages;
  };

  setWorkMaterials = (workMaterials: ListWorkMaterialDto[] | []) => {
    this.workMaterials = workMaterials;
  };

  setWorkServices = (workServices: ListWorkServiceDto[] | []) => {
    this.workServices = workServices;
  };
  /* getWorkPackages = async (changeId: number) => {
    try {
      this.setIsDataLoading(true);
      const { data: workPackages } =
        await workPackageApi.workPackagesControllerFindAll(undefined, [
          changeId,
        ]);

      if (workPackages) {
        this.setIsDataLoading(false);
        this.setWorkPackages(workPackages);
      }
    } catch (e) {
      this.setIsDataLoading(false);
      console.log(e);
    }
  }; */

  setUpdatedChange = (updatedChange: GetChangeDto) => {
    this.setChange(updatedChange);
  };

  pushNewWorkPackage = (newWorkPackage: ListWorkPackageDto) => {
    this.setWorkPackages([newWorkPackage, ...this.workPackages]);
  };

  pushNewServicePackage = (newServicePackage: ListWorkServiceDto) => {
    this.setWorkServices([newServicePackage, ...this.workServices]);
  };

  removeServicePackage = (removedServicePackageId: number) => {
    const index = this.workServices.findIndex(
      service => service.id === removedServicePackageId,
    );
    if (index !== -1) {
      this.workServices.splice(index, 1);
    }
  };

  replaceUpdatedServicePackage = (
    updatedServicePackage: ListWorkServiceDto,
  ) => {
    const index = this.workServices.findIndex(
      service => service.id === updatedServicePackage.id,
    );
    if (index !== -1) {
      this.workServices.splice(index, 1, updatedServicePackage);
    }
  };

  pushNewMaterialPackage = (newMaterialPackage: ListWorkMaterialDto) => {
    this.setWorkMaterials([newMaterialPackage, ...this.workMaterials]);
  };

  removeMaterialPackage = (removedMaterialPackageId: number) => {
    const index = this.workMaterials.findIndex(
      material => material.id === removedMaterialPackageId,
    );
    if (index !== -1) {
      this.workMaterials.splice(index, 1);
    }
  };

  replaceUpdatedMaterialPackage = (
    updatedMaterialPackage: ListWorkMaterialDto,
  ) => {
    const index = this.workMaterials.findIndex(
      material => material.id === updatedMaterialPackage.id,
    );
    if (index !== -1) {
      this.workMaterials.splice(index, 1, updatedMaterialPackage);
    }
  };

  removeWorkPackage = (removedWorkPackageId: number) => {
    const index = this.workPackages.findIndex(
      workPackage => workPackage.id === removedWorkPackageId,
    );
    this.workPackages.splice(index, 1);
  };

  replaceUpdatedWorkPackage = (updatedWorkPackage: ListWorkPackageDto) => {
    const index = this.workPackages.findIndex(
      workPackage => workPackage.id === updatedWorkPackage.id,
    );
    this.workPackages.splice(index, 1, updatedWorkPackage);
  };

  resetStore = () => {
    this.setChange(null);
    this.setWorkPackages([]);
    this.setWorkServices([]);
    this.setWorkMaterials([]);
  };

  get reducedWorkPackages() {
    return this.workPackages.reduce(
      (acc: ReducedWorkPackagesType, item: ListWorkPackageDto) => {
        if (!item.processId) return acc;
        acc[item.processId] = [...(acc[item.processId] || []), item];
        return acc;
      },
      {},
    );
  }

  get reducedServices() {
    return this.workServices.reduce(
      (acc: ReducedServicesType, item: ListWorkServiceDto) => {
        if (!item.processId) return acc;
        acc[item.processId] = [...(acc[item.processId] || []), item];
        return acc;
      },
      {},
    );
  }

  get reducedWorkMaterials() {
    return this.workMaterials.reduce(
      (acc: ReducedMaterialsType, item: ListWorkMaterialDto) => {
        if (!item.processId) return acc;
        acc[item.processId] = [...(acc[item.processId] || []), item];
        return acc;
      },
      {},
    );
  }

  get totalWorkPackageHoursOfUsedProcesses() {
    if (!this.change?.processes) return 0;
    const usedProcessIds = this.change.processes.map(p => p.id);
    return this.workPackages
      .filter(wp => wp.processId && usedProcessIds.includes(wp.processId))
      .reduce((sum, wp) => sum + (wp.hours || 0), 0);
  }

  getTotalWorkMaterialHoursOfUsedProcesses = (
    filterRole?: CreatorRole | undefined,
  ): number => {
    if (!this.change?.processes) return 0;

    const usedProcessIds = this.change.processes.map(p => p.id);

    return this.workMaterials
      .filter(
        mat =>
          mat.processId &&
          usedProcessIds.includes(mat.processId) &&
          (!filterRole || mat.creatorRole === filterRole),
      )
      .reduce((sum, mat) => sum + (mat.price || 0), 0);
  };

  getTotalWorkServicesHoursOfUsedProcesses = (
    filterRole?: CreatorRole | undefined,
  ) => {
    if (!this.change?.processes) return 0;

    const usedProcessIds = this.change.processes.map(p => p.id);

    return this.workServices
      .filter(
        svc =>
          svc.processId &&
          usedProcessIds.includes(svc.processId) &&
          (!filterRole || svc.creatorRole === filterRole),
      )
      .reduce((sum, svc) => sum + (svc.price || 0), 0);
  };

  getCalculatedHoursCosts = (released: boolean): number => {
    if (!this.change?.processes) return 0;
    const finalQuotationValueFromProcesses =
      this.change.processes &&
      this.change.processes.reduce((acc, process) => {
        const department = getDepartmentById(
          process.pId,
          this.change?.commonProject?.navProcessToCommonProjectRelations,
        );
        const hourlyRate = process.finalHourlyRate
          ? process.finalHourlyRate
          : department?.hourlyRate;

        const hoursToUse = released
          ? process.releasedHours
          : process.calculatedHours;

        if (hourlyRate && hoursToUse) {
          return acc + hourlyRate * hoursToUse;
        }

        return acc + 0;
      }, 0);

    return finalQuotationValueFromProcesses ?? 0;
  };

  getCalculatedHours = (released: boolean): number => {
    if (!this.change?.processes) return 0;

    const finalQuotationValueFromProcesses =
      this.change.processes &&
      this.change.processes.reduce((acc, process) => {
        const hoursToUse = released
          ? process.releasedHours
          : process.calculatedHours;

        if (hoursToUse) {
          return acc + hoursToUse;
        }

        return acc + 0;
      }, 0);

    return finalQuotationValueFromProcesses ?? 0;
  };
}

const initialChangeStore = new ChangeStore();

export const StoreContext = createContext<IChangeStore>(initialChangeStore);

export type StoreProviderProps = {
  children: React.ReactNode;
};

export const useChangeStore = (): IChangeStore => useContext(StoreContext);
